import { useState, useEffect } from "react";

import Box from "@mui/material/Box";

import { ImgResponsive } from "@/src/component/base/ImgResponsive";
import { CMSLogic } from "@/src/logic";
import { StrapiComponentType, ComponentType } from "@/src/type";

import { ButtonLinkModule, SectionModule, TextBreakpointModule, GapBreakpointModule } from "@/src/component/partial/__module__";

import styles from "./HeroRotateImg.module.scss";

// Swiper imports
import "swiper/css";
import "swiper/css/effect-fade";

import { Swiper, SwiperSlide } from "swiper/react";
import { EffectFade, Autoplay } from "swiper/modules";

interface HeroRotateImgProps extends ComponentType.DynamicPageSectionComponentProps<StrapiComponentType.Section.HeroRotateImg> {}

function Hero({
  lazy,
  section,
  slug,
  snapToGrid,
  bottomDrawerHeight,
  searchParams,
  mobileHint,
  deviceWidthHint,
  quality,
  layout,
}: HeroRotateImgProps) {
  const heroRotateImg: StrapiComponentType.Section.HeroRotateImg | undefined = section.type?.[0];
  const backgroundColor = CMSLogic.colorSelector(section?.background?.color);
  const speed = heroRotateImg?.rotateIntervalSeconds ?? 3000;
  const [swiper, setSwiper] = useState<any>(null);

  useEffect(() => {
    //don't trigger until swipper is fully mounted
    if (!swiper || typeof swiper?.destroy === "boolean" || !swiper?.autoplay) {
      return;
    }

    if (!swiper?.running) {
      swiper?.autoplay.start();
      swiper?.autoplay.resume();
    }
  }, [speed, swiper]);

  return (
    <SectionModule
      section={section}
      slug={slug}
      classes={{
        styledContainer: styles.container,
      }}
      stylePrefix="HeroRotateImg"
      lazy={lazy}
      styleContainerId={heroRotateImg.id}
      snapToGrid={snapToGrid}
      bottomDrawerHeight={bottomDrawerHeight}
      searchParams={searchParams}
      mobileHint={mobileHint}
      deviceWidthHint={deviceWidthHint}
      quality={quality}
      layout={layout}
    >
      <GapBreakpointModule
        id={`hero_rotate_img_${heroRotateImg.id}`}
        gapBreakpoints={heroRotateImg.gapBreakpoints}
        className={styles.content}
      >
        {!!heroRotateImg?.images?.data?.length ? (
          <Swiper
            className={styles.swiper}
            modules={[EffectFade, Autoplay]}
            effect="fade"
            autoplay={{ delay: speed, disableOnInteraction: false, pauseOnMouseEnter: false }}
            speed={heroRotateImg?.fadeSpeed ?? 500}
            loop={true}
            allowTouchMove={false}
            slidesPerView={1}
            centeredSlides
            onSwiper={setSwiper}
          >
            {heroRotateImg.images.data.map((image) => (
              <SwiperSlide
                key={image.id}
                style={{ backgroundColor }}
              >
                <ImgResponsive
                  className={styles.imageContainer}
                  src={image.attributes.url}
                  id={image.id.toString()}
                  objectFit="contain"
                  alt={image.attributes.name}
                  immediateLoad={lazy === "eager"}
                />
              </SwiperSlide>
            ))}
          </Swiper>
        ) : (
          <></>
        )}
        <TextBreakpointModule
          id={`hero_rotate_img_title_${heroRotateImg.id}`}
          text={section.title}
          className={styles.title}
        />
        {!!heroRotateImg?.cta?.length && (
          <Box className={styles.ctaContainer}>
            {heroRotateImg.cta.map((cta, index) => (
              <ButtonLinkModule
                key={index}
                buttonLink={cta}
                className={styles.cta}
              />
            ))}
          </Box>
        )}
      </GapBreakpointModule>
    </SectionModule>
  );
}

export default Hero;
